<script setup>
import { ref, onMounted, defineAsyncComponent } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import { getI18n } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'
import { entityChangeTypeEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { chatActionServiceEnum } from '@/stores/chat.store'

import { useConfirmedChatActions } from '@/composables/chat-actions'
import { useEntityChanges } from '@/composables/entity-changes'
import { useGridRef, useODataSource, useLookup, useDefaultGridOptions, refreshGridAsync, getCreatedRowId } from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'
import { useDataGridWidget } from '@/composables/data-grid-toolbar'
import { cloneCostReportRow, useLockedCostReportRow } from '@/composables/cost-report'
import { formatExpenseMoney, isClaimableExpense, unclaimedExpenseParams, useExpenseClaimActions } from '@/composables/expense-claim'

import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import CostReportRowEdit from '@/components/accounting/CostReportRowEdit.vue'

const ExpenseClaimDialog = defineAsyncComponent(() => import('@/components/accounting/ExpenseClaimDialog.vue'))

const props = defineProps({
  project: {
    type: Object,
    default: null,
  },
  staff: {
    type: Object,
    default: null,
  },
  expenseClaim: {
    type: Object,
    default: null,
  },
  claim: {
    type: Object,
    default: null,
  },
})
const selectedRowIds = defineModel('selectedRowIds', { type: Array, default: () => [] })

const emit = defineEmits(['row-saved', 'claim-saved'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const webSiteStore = useWebSiteStore()
const dataGridWidget = useDataGridWidget()
const { gridRef } = useGridRef()
const { isLocked } = useLockedCostReportRow()
const { canCreate } = useExpenseClaimActions()
const { costReportRowTypeEnum } = accountingStore

const selectedRows = ref([])
const editItem = ref(null)
const rowToDelete = ref(null)
const newClaim = ref(null)
const preselectedRowIds = ref([])
const newClaimButton = ref(null)

const isClaimSelector = !!props.claim
const isUnclaimed = !props.project && !props.expenseClaim && !isClaimSelector
const canSelectForClaim = isClaimSelector || isUnclaimed || !!props.project
let isSelectionRestored = false

const expenseParams = () => {
  if (isClaimSelector) return { type: costReportRowTypeEnum.EXPENSE, staff: props.claim.staff, claimable: true, ...(props.claim.id && { expense_claim: props.claim.id }) }
  if (props.expenseClaim) return { type: costReportRowTypeEnum.EXPENSE, expense_claim: props.expenseClaim.id }
  if (props.project) return { project: props.project.id, type: costReportRowTypeEnum.EXPENSE }
  return { ...unclaimedExpenseParams(), ...(props.staff && { staff: props.staff.id }) }
}

const dataSource = useODataSource(accountingStore.api.getAbsoluteUrl() + accountingStore.api.costReportRowsUrl, {
  customParamsFn: expenseParams,
})

const computedColumn = (options) => ({ allowFiltering: false, allowSorting: false, ...options })

const columns = [
  {
    type: 'buttons',
    buttons: [
      { hint: i18n.t('common.details'), icon: 'mdi mdi-magnify', visible: (e) => isLocked(e.row?.data), onClick: (e) => { editItem.value = { ...e.row.data } } },
      { hint: i18n.t('common.edit'), icon: 'edit', visible: (e) => !isLocked(e.row?.data), onClick: (e) => { editItem.value = { ...e.row.data } } },
      { hint: i18n.t('common.delete'), icon: 'trash', visible: (e) => !isLocked(e.row?.data), onClick: (e) => { rowToDelete.value = e.row.data } },
      { hint: i18n.t('common.clone'), icon: 'copy', visible: !props.expenseClaim, onClick: (e) => { editItem.value = cloneCostReportRow(e.row.data) } },
    ],
    fixed: false,
    visibleIndex: -1,
    showInColumnChooser: false,
    width: '7rem',
  },
  dataGridColumn.useDateColumn({ sortOrder: 'desc', width: '7rem' }),
  {
    dataField: 'tag',
    caption: i18n.t('common.expense'),
    lookup: useLookup({ dataSource: accountingStore.getLocalizedCostReportRowTags(), valueExpr: 'id', displayExpr: 'name' }),
  },
  dataGridColumn.useStaffColumn({ visible: !props.staff && !props.expenseClaim && !isClaimSelector && (!!props.project || webSiteStore.canApproveExpenseClaims) }),
  dataGridColumn.useProjectColumn({ visible: !props.project, fallbackText: row => row.project_name ?? i18n.t('common.generalExpenses') }),
  dataGridColumn.useCompanyColumn({ visible: !props.project, validationRules: [] }),
  { dataField: 'amount_reference', caption: i18n.t('common.amount'), dataType: 'number', calculateDisplayValue: row => formatExpenseMoney(row.amount_reference), width: '8rem' },
  dataGridColumn.useNotesColumn(),
  computedColumn({ dataField: 'row_files', caption: i18n.t('common.attachment', 2), cellTemplate: 'files-cell', width: '6rem', allowExporting: false }),
  dataGridColumn.useExpenseClaimColumn({ visible: !!props.project }),
]

const refreshAsync = async(createdRowId = null) => {
  if (!isClaimSelector) gridRef.value?.instance.clearSelection()
  await refreshGridAsync(gridRef, createdRowId)
  emit('row-saved', props.project?.id)
}

useEntityChanges([entityChangeTypeEnum.COST_REPORT_ROW], (change) => refreshAsync(change.created ? change.id : null), () => editItem.value !== null)
useConfirmedChatActions(chatActionServiceEnum.EXPENSES, () => refreshAsync())

const openNewClaim = () => {
  const staff = selectedRows.value[0]?.staff ?? props.staff?.id ?? webSiteStore.staffId
  if (!canCreate(staff)) return
  preselectedRowIds.value = selectedRows.value.map(row => row.id)
  newClaim.value = { staff }
}

const onClaimSaved = async(claim) => {
  newClaim.value = null
  await refreshAsync()
  if (claim) emit('claim-saved', claim)
}

const onRowSaved = async(saved) => {
  const createdId = getCreatedRowId(editItem.value, saved)
  editItem.value = null
  await refreshAsync(createdId)
}

const onDeleteConfirmedAsync = async() => {
  await accountingStore.deleteCostReportRowAsync(rowToDelete.value.id)
  rowToDelete.value = null
  await refreshAsync()
}

const newRowDefaults = () => ({
  type: costReportRowTypeEnum.EXPENSE,
  ...(props.claim && { staff: props.claim.staff }),
  ...(props.staff && { staff: props.staff.id }),
})

const gridOptions = useDefaultGridOptions({
  id: isClaimSelector ? 'expense-claim-rows-grid-v1' : props.project ? `cost-report-expenses-grid-v4-${props.project.id}` : isUnclaimed ? 'unclaimed-expenses-grid-v1' : 'expense-claim-detail-rows-grid-v1',
  dataSource,
  columns,
  editing: { allowAdding: false, allowUpdating: false, allowDeleting: false },
  clearFilterButton: true,
  selection: canSelectForClaim ? { mode: 'multiple', showCheckBoxesMode: 'always', selectAllMode: isClaimSelector ? 'allPages' : 'page' } : { mode: 'none' },
  onSelectionChanged: (e) => {
    if (isClaimSelector) {
      selectedRowIds.value = e.selectedRowKeys
      return
    }
    const unclaimableKeys = e.selectedRowsData.filter(row => !isClaimableExpense(row)).map(row => row.id)
    if (unclaimableKeys.length) {
      e.component.deselectRows(unclaimableKeys)
      return
    }
    selectedRows.value = e.selectedRowsData
    newClaimButton.value?.option('disabled', new Set(selectedRows.value.map(row => row.staff)).size > 1)
    newClaimButton.value?.option('text', selectedRows.value.length ? `${i18n.t('common.expenseClaimNew')} (${selectedRows.value.length})` : i18n.t('common.expenseClaimNew'))
  },
  onContentReady: (e) => {
    if (!isClaimSelector || isSelectionRestored) return
    isSelectionRestored = true
    e.component.selectRows(selectedRowIds.value)
  },
  onCellPrepared: (e) => {
    if (!isClaimSelector && e.rowType === 'data' && e.column.command === 'select' && !isClaimableExpense(e.data)) {
      e.cellElement.querySelector('.dx-select-checkbox')?.classList.add('invisible')
    }
  },
  onRowDblClick: (e) => { if (e.rowType === 'data') editItem.value = { ...e.data } },
  addButton: { visible: !props.expenseClaim, onClick: () => { editItem.value = newRowDefaults() } },
  isDetailGrid: true,
  exportWidgets: [
    dataGridWidget.actionWidget(newClaimButton, {
      icon: 'mdi mdi-receipt-text-plus-outline',
      text: i18n.t('common.expenseClaimNew'),
      disabled: false,
      visible: (isUnclaimed || !!props.project) && (webSiteStore.hasStaff || webSiteStore.canApproveExpenseClaims),
      onClick: openNewClaim,
    }),
  ],
})

const loadTagsAsync = async() => {
  if (!accountingStore.costReportRowTags.length) await accountingStore.fetchCostReportRowTagsAsync({}, true)
  gridRef.value?.instance.columnOption('tag', 'lookup.dataSource', accountingStore.getLocalizedCostReportRowTags())
}

const loadReferencesAsync = async() => {
  await Promise.all([
    !planningStore.projects.length && planningStore.fetchProjectsAsync({}, true),
    !props.project && !planningStore.companies.length && planningStore.fetchCompaniesAsync({}, true),
    !planningStore.staff.length && planningStore.fetchStaffsAsync({}, true),
  ])
  const grid = gridRef.value?.instance
  grid?.columnOption('company', 'lookup.dataSource', planningStore.companies)
  grid?.columnOption('staff', 'lookup.dataSource', planningStore.staff)
  grid?.repaint()
}

onMounted(() => Promise.all([
  loadTagsAsync(),
  loadReferencesAsync(),
]))
</script>

<template>
  <div class="column no-wrap q-px-sm">
    <confirm-dialog
      :model-value="rowToDelete !== null"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @update:model-value="value => { if (!value) rowToDelete = null }"
      @confirm="onDeleteConfirmedAsync"
    />
    <q-dialog :model-value="editItem !== null" class="dialog-md" persistent>
      <cost-report-row-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :project="props.project"
        @update="onRowSaved"
        @cancel="editItem = null"
      />
    </q-dialog>
    <expense-claim-dialog
      v-if="newClaim !== null"
      :claim="newClaim"
      :preselected-row-ids="preselectedRowIds"
      :project-id="props.project?.id ?? null"
      @saved="onClaimSaved"
      @cancel="newClaim = null"
    />
    <dx-data-grid ref="gridRef" v-bind="gridOptions">
      <template #files-cell="{ data: templateOptions }">
        <q-icon
          v-if="templateOptions.data.row_files?.length"
          name="mdi-paperclip"
          color="grey-7"
          size="xs"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
